import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ClipboardList, Mail, Phone, User, Truck, Target, Loader2, Sparkles,
  CheckCircle, AlertCircle, RefreshCw, ArrowRight, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_BADGES = {
  build: { label: 'Build', color: '#F59E0B' },
  protect: { label: 'Protect', color: '#3B82F6' },
  grow: { label: 'Grow', color: '#10B981' },
};

const TRUCK_LABELS = {
  '1': 'Owner-Op',
  '2-5': 'Small Fleet (2-5)',
  '5+': 'Fleet 5+',
};

const BOTTLENECK_LABELS = {
  compliance: 'DOT regulations & paperwork',
  lanes: 'Finding high-paying lanes',
  ifta_audits: 'IFTA, audits, compliance',
};

/**
 * Admin section: Carrier Applications (formerly the public form, now internal)
 * - Shows all intake submissions
 * - Filter by status (step_1_complete | complete)
 * - One-click promote to full application pipeline
 */
const AdminIntakeReviewSection = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'complete' | 'step_1_complete'
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/intake/admin/all?limit=200${filter !== 'all' ? `&status=${filter}` : ''}`),
        axios.get(`${API}/intake/admin/stats`),
      ]);
      setItems(a.data);
      setStats(s.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = items.filter(i => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (i.full_name || '').toLowerCase().includes(q)
      || (i.email || '').toLowerCase().includes(q)
      || (i.phone || '').toLowerCase().includes(q);
  });

  const promote = async (id) => {
    try {
      await axios.post(`${API}/intake/admin/${id}/promote-to-application`);
      await load();
      alert('Promoted to full application pipeline. Find it under the existing Carrier Applications admin section.');
    } catch (e) {
      alert('Promote failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-intake-review">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-[#D4AF37]" />
          Carrier Applications
        </h2>
        <p className="text-white/50 text-sm">Intake submissions from the public Join The Hive form.</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total" value={stats.total_submissions} icon={ClipboardList} color="#D4AF37" />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle} color="#10B981" />
          <StatCard label="Drop-off (Step 2)" value={stats.abandoned_after_step_1} icon={AlertCircle} color="#F59E0B" />
          <StatCard label="Completion Rate" value={`${stats.completion_rate}%`} icon={Sparkles} color="#3B82F6" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
            data-testid="intake-search"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
          data-testid="intake-filter"
        >
          <option value="all">All Submissions</option>
          <option value="complete">Completed (both steps)</option>
          <option value="step_1_complete">Drop-off (only step 1)</option>
        </select>
        <button onClick={load} className="p-3 hover:bg-white/10 border border-white/10" title="Refresh">
          <RefreshCw className={`w-4 h-4 text-white/60 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* List */}
      <div className="bg-[#121212] border border-white/10 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="w-10 h-10 text-white/30 mx-auto mb-3" />
            <p className="text-white/60">No intake submissions match this filter yet.</p>
          </div>
        ) : (
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-white/10 text-white/50 text-xs uppercase tracking-wider">
                <th className="text-left py-3 px-4">Contact</th>
                <th className="text-left py-3 px-4">Stage / Package</th>
                <th className="text-left py-3 px-4">Fleet</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Submitted</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const stage = STAGE_BADGES[it.stage] || null;
                return (
                  <tr key={it.submission_id} className={`border-b border-white/5 hover:bg-white/5 ${it.is_hot_lead ? 'bg-[#10B981]/[0.04]' : ''}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 mb-1">
                        {it.is_hot_lead && (
                          <span title="High Priority — Long-term partner + 5+ trucks" className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 bg-green-500/15 text-green-500 border border-green-500/40">
                            🔥 Hot
                          </span>
                        )}
                        <p className="text-white text-sm font-medium">{it.full_name}</p>
                      </div>
                      <p className="text-white/40 text-xs">{it.email}</p>
                      <p className="text-white/40 text-xs">{it.phone}</p>
                    </td>
                    <td className="py-3 px-4">
                      {stage ? (
                        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5"
                          style={{ color: stage.color, backgroundColor: `${stage.color}15`, border: `1px solid ${stage.color}40` }}>
                          {stage.label}
                        </span>
                      ) : (
                        <span className="text-white/30 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-white/60 text-xs">{TRUCK_LABELS[it.trucks] || '—'}</td>
                    <td className="py-3 px-4">
                      {it.status === 'complete' ? (
                        <span className="text-green-500 text-xs font-bold uppercase">Complete</span>
                      ) : (
                        <span className="text-yellow-500 text-xs font-bold uppercase">Drop-off</span>
                      )}
                      {it.promoted_application_id && (
                        <p className="text-[#D4AF37] text-[10px] uppercase mt-0.5">✓ Promoted</p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-white/50 text-xs">
                      {new Date(it.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSelected(it)}
                          className="px-3 py-1.5 bg-white/10 text-white text-xs hover:bg-white/20"
                          data-testid={`intake-view-${it.submission_id}`}
                        >
                          View
                        </button>
                        {it.status === 'complete' && !it.promoted_application_id && (
                          <button
                            onClick={() => promote(it.submission_id)}
                            className="px-3 py-1.5 bg-[#D4AF37] text-black text-xs font-bold hover:bg-[#F5D47B]"
                            title="Convert to full application for approval pipeline"
                            data-testid={`intake-promote-${it.submission_id}`}
                          >
                            Promote
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selected && <IntakeDetailModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#121212] border border-white/10 p-4">
    <div className="flex items-center justify-between mb-2 gap-2">
      <span className="text-white/50 text-xs uppercase tracking-wider truncate">{label}</span>
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
    </div>
    <p className="text-2xl font-black text-white">{value}</p>
  </div>
);

const IntakeDetailModal = ({ item, onClose }) => {
  const stage = STAGE_BADGES[item.stage];
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0D0D0D] border border-[#D4AF37]/40 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">{item.full_name}</h3>
            <p className="text-white/50 text-sm">Submitted {new Date(item.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-2xl">×</button>
        </div>
        <div className="p-5 space-y-4">
          <DetailRow icon={Mail} label="Email" value={item.email} />
          <DetailRow icon={Phone} label="Phone" value={item.phone} />
          {item.ref_code && <DetailRow icon={ArrowRight} label="Referred By" value={item.ref_code} />}

          {item.status === 'complete' && (
            <>
              <hr className="border-white/10" />
              <DetailRow icon={Target} label="Stage" value={
                stage ? <span style={{ color: stage.color }}>{stage.label} (Recommended: {item.recommended_package})</span> : item.stage
              } />
              <DetailRow icon={Truck} label="Fleet Size" value={TRUCK_LABELS[item.trucks] || item.trucks} />
              <DetailRow icon={AlertCircle} label="Bottleneck" value={BOTTLENECK_LABELS[item.bottleneck] || item.bottleneck} />
              <DetailRow icon={CheckCircle} label="Long-term Partner?" value={item.long_term_partner === 'yes' ? 'Yes' : 'No'} />
            </>
          )}

          {item.promoted_application_id && (
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-3 text-sm text-[#D4AF37]">
              ✓ Promoted to full application. Application ID: <span className="font-mono">{item.promoted_application_id}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-white break-words">{value}</p>
    </div>
  </div>
);

export default AdminIntakeReviewSection;
